import {
  Button,
  Table,
  Modal,
  Progress,
  Tooltip,
  message,
} from '@surecompanies/core_components'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { TableProps } from '@surecompanies/core_components'
import type { ColumnType, SorterResult } from 'antd/es/table/interface'
import { convertToLocalFromUTCISODate } from 'helpers/datetime'
import type { PlanManagementTaskDto } from 'enrollment-platform/pages/PlanManagementV2/api/dto/plan-management-task.dto'
import { PlanManagementTaskStatusEnum } from 'enrollment-platform/pages/PlanManagementV2/api/enums/plan-management-task-status.enum'
import usePlanDataSnapshotList, {
  SortField,
} from 'enrollment-platform/pages/PlanManagementV2/hooks/usePlanDataSnapshotList'
import {
  deleteTask,
  fetchPlanSnapshot,
} from 'enrollment-platform/pages/PlanManagementV2/api/plan-management-tasks-api'
import DownloadSurecoCsvModal from './DownloadSurecoCsvModal'
import styles from '../PlanManagementV3.module.scss'

const TOOLTIP_OVERLAY_STYLE = {
  maxWidth: 'none',
  whiteSpace: 'nowrap',
} as const

const POINTER_STYLE = { cursor: 'pointer' } as const

const STATUS_PILL_CLASS = {
  success: styles.statusPillSuccess,
  error: styles.statusPillError,
  pending: styles.statusPillPending,
  neutral: styles.statusPillNeutral,
} as const

const PENDING_TONE_COLOR = '#92400e'

function StatusPill({
  label,
  variant,
}: {
  label: string
  variant: keyof typeof STATUS_PILL_CLASS
}) {
  return (
    <span className={`${styles.statusPill} ${STATUS_PILL_CLASS[variant]}`}>
      {label}
    </span>
  )
}

function FetchingStatus({ percent }: { percent: number | null | undefined }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Progress
        type="circle"
        percent={percent ?? 0}
        size={16}
        showInfo={false}
        strokeWidth={14}
        strokeColor={PENDING_TONE_COLOR}
        trailColor="#f1f2f4"
      />
      <span className={styles.statusFetchingLabel}>Fetching</span>
    </span>
  )
}

function renderStatusTag(row: PlanManagementTaskDto) {
  const status = row.status
  switch (status) {
    case PlanManagementTaskStatusEnum.PENDING_FETCH:
      return <FetchingStatus percent={row.progress} />
    case PlanManagementTaskStatusEnum.FAIL_FETCH:
      return <StatusPill label="Failed" variant="error" />
    case PlanManagementTaskStatusEnum.FAIL_SYNC:
      return <StatusPill label="Fail Sync" variant="error" />
    case PlanManagementTaskStatusEnum.MIGRATING:
      return <StatusPill label="Migrating" variant="pending" />
    case PlanManagementTaskStatusEnum.MIGRATE_FAILED:
      return <StatusPill label="Migrate Failed" variant="error" />
    case PlanManagementTaskStatusEnum.MIGRATED:
      return <StatusPill label="Migrated" variant="neutral" />
    case PlanManagementTaskStatusEnum.PENDING_COMPARE:
      return <StatusPill label="Analyzing" variant="pending" />
    case PlanManagementTaskStatusEnum.FAIL_COMPARE:
      return <StatusPill label="Fail Analyze" variant="error" />
    case PlanManagementTaskStatusEnum.SUCCESS:
      return <StatusPill label="Success" variant="success" />
    default:
      return <StatusPill label={status} variant="neutral" />
  }
}

export default function FetchLogTable() {
  const queryClient = useQueryClient()
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] =
    useState<PlanManagementTaskDto | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDateShiftClick = useCallback(
    (row: PlanManagementTaskDto, e: React.MouseEvent) => {
      if (!e.shiftKey) return
      e.stopPropagation()
      setDeleteTarget(row)
    },
    []
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteTask(deleteTarget.id)
      await queryClient.invalidateQueries({
        queryKey: ['planDataSnapshotList'],
      })
      setDeleteTarget(null)
      message.success('Task deleted')
    } catch {
      message.error('Failed to delete task')
    } finally {
      setIsDeleting(false)
    }
  }, [deleteTarget, queryClient])

  const {
    data,
    total,
    isLoading,
    error,
    page,
    pageSize,
    sort,
    order,
    hasPendingTask,
    handlePageChange,
    handleSortChange,
  } = usePlanDataSnapshotList()

  const fetchMutation = useMutation({
    mutationFn: () => fetchPlanSnapshot({}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['planDataSnapshotList'] })
    },
    onError: () => {
      message.error('Could not start fetch. Please try again.')
    },
  })

  const columns = useMemo<ColumnType<PlanManagementTaskDto>[]>(
    () => [
      {
        title: 'Time Stamp',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: false,
        sortOrder:
          sort === 'createdAt'
            ? order === 'ASC'
              ? 'ascend'
              : 'descend'
            : undefined,
        render: (value: string, row: PlanManagementTaskDto) =>
          value ? (
            <Tooltip
              title={`ID: ${row.id}`}
              placement="topLeft"
              overlayStyle={TOOLTIP_OVERLAY_STYLE}
            >
              <span
                style={POINTER_STYLE}
                onClick={(e) => handleDateShiftClick(row, e)}
              >
                {convertToLocalFromUTCISODate(value)}
              </span>
            </Tooltip>
          ) : (
            ''
          ),
      },
      {
        title: 'Plan Year',
        dataIndex: 'planYear',
        key: 'planYear',
        width: 100,
      },
      {
        title: 'Admin',
        dataIndex: 'fetchedBy',
        key: 'fetchedBy',
        render: (value: string | null | undefined) => value ?? 'System',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: false,
        sortOrder:
          sort === 'status'
            ? order === 'ASC'
              ? 'ascend'
              : 'descend'
            : undefined,
        render: (_: unknown, row: PlanManagementTaskDto) =>
          renderStatusTag(row),
      },
    ],
    [sort, order, handleDateShiftClick]
  )

  const handleTableChange: TableProps<PlanManagementTaskDto>['onChange'] =
    useCallback(
      (_, __, sorter, extra) => {
        if (extra?.action === 'sort') {
          const s = sorter as SorterResult<PlanManagementTaskDto>
          if (s.field && s.order) {
            handleSortChange(
              s.field as SortField,
              s.order === 'ascend' ? 'ASC' : 'DESC'
            )
          } else if (sort === 'createdAt' && order === 'DESC') {
            handleSortChange('createdAt', 'ASC')
          } else {
            handleSortChange('createdAt', 'DESC')
          }
        }
      },
      [handleSortChange, sort, order]
    )

  useEffect(() => {
    if (error) {
      console.error('Failed to load plan data snapshots', error)
      message.error('An error occurred. Please try again.')
    }
  }, [error])

  const isFetchDisabled = hasPendingTask || fetchMutation.isPending

  return (
    <div className={styles.card}>
      <DownloadSurecoCsvModal
        open={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
      />

      <Modal
        open={!!deleteTarget}
        title="Delete task?"
        centered
        maskClosable={false}
        onCancel={() => setDeleteTarget(null)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={isDeleting}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>,
        ]}
      >
        <p>
          This will permanently delete task <strong>{deleteTarget?.id}</strong>{' '}
          (plan year {deleteTarget?.planYear}) and all associated S3 files. This
          cannot be undone.
        </p>
      </Modal>

      <div className={styles.cardHeader}>
        <h1 className={styles.cardHeaderTitle}>Fetch IDEON</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip
            title={
              hasPendingTask ? 'A fetch is already in progress' : undefined
            }
          >
            <Button
              disabled={isFetchDisabled}
              loading={fetchMutation.isPending}
              onClick={() => fetchMutation.mutate()}
            >
              Fetch
            </Button>
          </Tooltip>
          <Button type="primary" onClick={() => setIsDownloadModalOpen(true)}>
            Download
          </Button>
        </div>
      </div>

      <Table
        className={styles.flatTable}
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
          onChange: handlePageChange,
        }}
        sortDirections={['ascend', 'descend']}
        showSorterTooltip={false}
        locale={{ emptyText: 'No Data' }}
      />
    </div>
  )
}
